import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

export default function Testimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [page, setPage] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState('input');
  const [testimonialId, setTestimonialId] = useState(null);
  const [otp, setOtp] = useState('');
  const [showConsent, setShowConsent] = useState(false);

const load = useCallback(async () => {
  const { data, error } = await supabase
    .from('testimonials')
    .select('id, name, message, created_at, email')
    .eq('is_verified', true)
    .order('created_at', { ascending: false });

  if (!error && data) {
    // Deduplicate by email: keep only the latest
    const map = new Map();
    for (const t of data) {
      if (!map.has(t.email)) {
        map.set(t.email, t);
      }
    }
    const uniqueByEmail = Array.from(map.values()).slice(0, 6);
    setTestimonials(uniqueByEmail);
    setPage(0);
  }
}, []);

  useEffect(() => {
    load();
  }, [load]);

  const perPage = 3;
  const pages = Math.ceil(testimonials.length / perPage);
  const handleNext = useCallback(() => setPage((p) => (p + 1) % pages), [pages]);
  const handlePrev = () => setPage((p) => (p - 1 + pages) % pages);

  useEffect(() => {
    if (pages <= 1) return;
    const timer = setInterval(handleNext, 5000);
    return () => clearInterval(timer);
  }, [handleNext, pages]);

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name || !form.email || !form.message) {
      setError('All fields are required.');
      return;
    }
    setLoading(true);

const today = new Date();
today.setHours(0, 0, 0, 0);

const { data: existing, error: checkErr } = await supabase
  .from('testimonials')
  .select('id')
  .eq('email', form.email.trim())
  .gte('created_at', today.toISOString());

if (checkErr) throw checkErr;

if (existing.length >= 2) {
  setError('You can only submit 2 testimonials per day.');
  setLoading(false);
  return;
}
    try {
      const { data, error: insErr } = await supabase
        .from('testimonials')
        .insert([{ name: form.name.trim(), email: form.email.trim(), message: form.message.trim() }])
        .select('id')
        .single();

      if (insErr || !data) throw insErr;

      setTestimonialId(data.id);

      const { error: fnError } = await supabase.functions.invoke('sendTestimonialOtp', {
        body: JSON.stringify({ testimonial_id: data.id, email: form.email }),
      });

      if (fnError) throw fnError;


      setStep('otp');
    } catch (err) {
      console.error(err);
      setError('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data: raw, error } = await supabase.functions.invoke('verifyTestimonialOtp', {
        body: JSON.stringify({ testimonial_id: testimonialId, code: otp }),
      });

      if (error) throw error;

      const result = JSON.parse(raw);
      if (result.verified) {
        setShowConsent(true);
        return;
        const { error: updateErr } = await supabase.from('testimonials').update({ is_verified: true }).eq('id', testimonialId);
        if (updateErr) console.error('Update is_verified failed:', updateErr);
        setStep('submitted');
        setShowConsent(false);
        load();
      } else {
        setError('Invalid or expired code.');
      }
    } catch (err) {
      setError('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const visible = testimonials.slice(page * perPage, page * perPage + perPage);

  return (
    <section className="bg-[#f9f4ef] py-16 px-4 md:px-20">
      <h2 className="text-3xl font-bold text-[#7C2D12] text-center mb-8">
        What People Are Saying
      </h2>

      <div className="relative max-w-[960px] mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {visible.map((t, i) => (
            <div
              key={t.id}
              className={`
                \${i === 1 ? 'bg-orange-50' : 'bg-white'}
                p-6 rounded-lg shadow 
                transition transform hover:scale-105 hover:shadow-lg
                min-h-[320px] flex flex-col
              `}
            >
              <div className="flex justify-center mb-4">
                <img
                  src="/assets/default-profile.png"
                  alt={t.name}
                  className="w-20 h-20 rounded-full object-cover ring-2 ring-orange-200"
                />
              </div>
              <p className="text-gray-700 italic mb-4 flex-grow leading-relaxed">
                “{t.message}”
              </p>
              <p className="text-gray-900 font-semibold text-center mt-2">
                – {t.name}
              </p>
            </div>
          ))}
        </div>

        {pages > 1 && (
          <>
            <button
              onClick={handlePrev}
              className="absolute -left-6 top-1/2 transform -translate-y-1/2 p-2 bg-white/90 rounded-full shadow hover:bg-white"
              aria-label="Previous testimonials"
            >
              <ChevronLeftIcon className="w-6 h-6 text-[#7C2D12]" />
            </button>
            <button
              onClick={handleNext}
              className="absolute -right-6 top-1/2 transform -translate-y-1/2 p-2 bg-white/90 rounded-full shadow hover:bg-white"
              aria-label="Next testimonials"
            >
              <ChevronRightIcon className="w-6 h-6 text-[#7C2D12]" />
            </button>
          </>
        )}

        <div className="flex justify-center mt-6 space-x-2">
          {[...Array(pages)].map((_, idx) => (
            <span
              key={idx}
              className={`w-3 h-3 rounded-full \${idx === page ? 'bg-orange-500' : 'bg-gray-300'}`}
            />
          ))}
        </div>
      </div>

      <div className="max-w-[960px] mx-auto mt-8">
        {!showForm ? (
          <div className="text-center">
            <button
              onClick={() => setShowForm(true)}
              className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition"
            >
              Write a Testimonial
            </button>
          </div>
        ) : step === 'otp' ? (
          <form onSubmit={handleVerify} className="bg-white p-8 rounded-lg shadow-md max-w-lg mx-auto space-y-4">
            <p className="text-sm text-gray-700">A verification code has been sent to your email.</p>
            {error && <p className="text-red-600 text-sm">{error}</p>}
            <input
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              placeholder="Enter OTP"
              className="w-full border px-3 py-2 rounded"
            />
            <div className="flex justify-end">
              <button
                type="submit"
                className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600"
              >
                Verify
              </button>
            </div>
          </form>
        ) : step === 'submitted' ? (
          <div className="text-center text-green-600 font-semibold">
            ✅ Thank you! Your testimonial has been submitted.
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white p-8 rounded-lg shadow-md max-w-lg mx-auto space-y-4">
            {error && <p className="text-red-600 text-sm">{error}</p>}
            <input
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Your Name"
              className="w-full border px-3 py-2 rounded"
            />
            <input
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              placeholder="Your Email"
              className="w-full border px-3 py-2 rounded"
            />
            <textarea
              name="message"
              value={form.message}
              onChange={handleChange}
              rows={4}
              placeholder="Your Testimonial"
              className="w-full border px-3 py-2 rounded"
            />
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setError('');
                }}
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50"
              >
                {loading ? 'Submitting…' : 'Submit'}
              </button>
            </div>
          </form>
        )}
      </div>
    
      {showConsent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-lg w-[90%] max-w-md">
            <h3 className="text-lg font-semibold mb-2">Consent to Store Information</h3>
            <p className="text-sm text-gray-700 mb-4">
              By clicking Agree, you consent to us storing your testimonial and email address for display and moderation purposes.
            </p>
            <div className="flex justify-end space-x-2">
              <button
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                onClick={() => setShowConsent(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600"
                onClick={async () => {
                  const { error: updateErr } = await supabase
                    .from('testimonials')
                    .update({ is_verified: true })
                    .eq('id', testimonialId);
                  if (updateErr) {
                    console.error('Update is_verified failed:', updateErr);
                    setError('Consent failed. Try again.');
                  } else {
                    setStep('submitted');
                    load();
                  }
                  setShowConsent(false);
                }}
              >
                I Agree
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
